import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';

const SECTIONS = [
  'Family & Child Info',
  'Diagnoses & Development',
  'Medical & Health',
  'School & Learning',
  'Behavior & Regulation',
  'Communication',
  'Medications & Supplements',
  'Why Unbox\'D'
];

const DIAGNOSES = [
  'Autism (ASD)', 'ADHD', 'Sensory Processing Disorder', 'Speech/Language Delay',
  'Intellectual Disability', 'Anxiety', 'OCD', 'Down Syndrome',
  'Epilepsy/Seizure Disorder', 'Genetic Disorder', 'Apraxia',
  'Auditory Processing Disorder', 'Visual Processing Disorder',
  'Cerebral Palsy', 'Learning Disability', 'Other'
];

const EVALUATIONS = [
  'Neuropsych', 'Developmental Pediatrician', 'Genetics', 'Speech/Language',
  'Occupational Therapy', 'Physical Therapy', 'Audiological', 'Vision', 'Other'
];

const THERAPIES = [
  'Speech Therapy', 'Occupational Therapy', 'Physical Therapy', 'ABA',
  'Feeding Therapy', 'Music Therapy', 'Equine Therapy', 'Other'
];

const GI_SYMPTOMS = [
  'Constipation', 'Diarrhea', 'Reflux', 'Bloating', 'Abdominal pain',
  'Food intolerances', 'Vomiting', 'None'
];

const SCHOOL_SERVICES = [
  'Speech', 'Occupational Therapy', 'Physical Therapy', 'Resource Room',
  'Aide/Paraprofessional', 'Social Skills Group', 'Counseling', 'Other'
];

const ACCOMMODATIONS = [
  'Extended time', 'Preferential seating', 'Sensory breaks', 'Visual supports',
  'Modified assignments', 'Reduced homework', 'Testing accommodations',
  'Noise-canceling headphones', 'Fidget tools', 'Other'
];

const BEHAVIOR_CONCERNS = [
  'Meltdowns', 'Aggression', 'Self-injury', 'Elopement/running',
  'Rigidity/inflexibility', 'Anxiety', 'Stimming', 'Picky/restricted eating',
  'Toileting challenges', 'Sleep difficulties', 'Tantrums',
  'Defiance/non-compliance', 'Social difficulties', 'Other'
];

const SUPPLEMENTS = [
  'Omega-3/Fish Oil', 'Magnesium', 'Vitamin D', 'Probiotics', 'B Vitamins',
  'Iron', 'Zinc', 'Methylfolate', 'CoQ10', 'Glutathione', 'NAC',
  'Curcumin/Turmeric', 'CBD', 'Melatonin', 'Vitamin C',
  'Digestive Enzymes', 'Colostrum', 'Other'
];

const CheckboxGroup = ({ options, selected, onChange, label }) => (
  <div className="space-y-2">
    {label && <Label className="text-sm font-medium">{label}</Label>}
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
      {options.map(opt => (
        <label key={opt} className={`flex items-center gap-2 px-3 py-2 rounded-full border cursor-pointer text-sm transition-colors ${selected.includes(opt) ? 'bg-[#C97B5A]/10 border-[#C97B5A] text-[#C97B5A]' : 'border-gray-200 hover:border-gray-300'}`}>
          <Checkbox
            checked={selected.includes(opt)}
            onCheckedChange={(checked) => {
              if (checked) onChange([...selected, opt]);
              else onChange(selected.filter(s => s !== opt));
            }}
            className="sr-only"
          />
          <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected.includes(opt) ? 'border-[#C97B5A] bg-[#C97B5A]' : 'border-gray-300'}`}>
            {selected.includes(opt) && <Check className="w-3 h-3 text-white" />}
          </span>
          {opt}
        </label>
      ))}
    </div>
  </div>
);

const Field = ({ label, required, children }) => (
  <div className="space-y-1">
    <Label className="text-sm font-medium">{label}{required && <span className="text-red-400 ml-1">*</span>}</Label>
    {children}
  </div>
);

const IntakeForm = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [existingForm, setExistingForm] = useState(null);

  const [form, setForm] = useState({
    // Section 1: Family & Child Info
    parentName: '', relationship: '', phone: '', address: '',
    childFirstName: '', childLastName: '', childDob: '', childAge: '',
    childPronouns: '', householdMembers: '', siblings: '', familyNotes: '',
    // Section 2: Diagnoses & Development
    diagnoses: [], diagnosisOther: '', ageAtDiagnosis: '', diagnosingProvider: '',
    evaluations: [], evaluationResults: '',
    therapies: [], therapyOther: '', therapyDetails: '',
    gestationalAge: '', birthComplications: '', nicuStay: '',
    ageWalked: '', ageFirstWords: '', regression: '', regressionDetails: '',
    familyHistory: '',
    // Section 3: Medical & Health
    currentSpecialists: '', giSymptoms: [], giOther: '',
    sleepBedtime: '', sleepHours: '', nightWaking: '', sleepAids: '',
    immuneHistory: '', foodAllergies: '', currentDiet: '', environmentalConcerns: '',
    // Section 4: School & Learning
    schoolName: '', grade: '', schoolType: '',
    iepStatus: '', iepGoals: '',
    schoolServices: [], schoolServicesOther: '',
    accommodations: [], accommodationDetails: '',
    academicStrengths: '', academicChallenges: '',
    // Section 5: Behavior & Regulation
    behaviorConcerns: [], behaviorOther: '',
    behaviorFrequency: '', knownTriggers: '', strategiesTried: '',
    sensoryProfile: '', sensoryDetails: '',
    meltdownDescription: '', meltdownHelps: '',
    childStrengths: '',
    // Section 6: Communication
    verbalLevel: '', receptiveLanguage: '', expressiveConcerns: '',
    aacUse: '', aacDetails: '', socialCommunication: '', slpHistory: '',
    // Section 7: Medications & Supplements
    medications: '', supplements: [], supplementOther: '', dietaryProtocol: '',
    // Section 8: Why Unbox'D
    howHeard: '', whyNow: '', topGoals: '', timeline: '', anythingElse: '',
    consent: false
  });

  useEffect(() => {
    const fetchExisting = async () => {
      try {
        const res = await API.get('/intake-form');
        if (res.data && res.data.form_id) {
          setExistingForm(res.data);
        }
      } catch (e) { /* no form yet */ }
    };
    fetchExisting();
  }, []);

  const u = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async () => {
    if (!form.consent) {
      toast.error('Please agree to the consent statement');
      return;
    }
    if (!form.childFirstName || !form.parentName) {
      toast.error('Please fill in required fields (Parent name, Child name)');
      return;
    }
    setSaving(true);
    try {
      await API.post('/intake-form', form);
      setSubmitted(true);
      toast.success('Intake form submitted successfully!');
    } catch (err) {
      toast.error('Failed to submit form');
    } finally {
      setSaving(false);
    }
  };

  if (existingForm && !submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center" data-testid="intake-already-submitted">
          <CardContent className="pt-8 pb-8 space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold">Intake Form Already Submitted</h2>
            <p className="text-gray-500 text-sm">You've already completed your intake form. Coach Robin will review it before your first session.</p>
            <Button onClick={() => navigate('/dashboard')} className="bg-[#C97B5A] hover:bg-[#b86a4a]">Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center" data-testid="intake-success">
          <CardContent className="pt-8 pb-8 space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold">Intake Form Submitted!</h2>
            <p className="text-gray-500 text-sm">Thank you for completing the intake form. Coach Robin will review everything before your first session.</p>
            <Button onClick={() => navigate('/dashboard')} className="bg-[#C97B5A] hover:bg-[#b86a4a]">Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const renderSection = () => {
    switch (step) {
      case 0: return <Section1 form={form} setForm={setForm} u={u} user={user} />;
      case 1: return <Section2 form={form} setForm={setForm} u={u} />;
      case 2: return <Section3 form={form} setForm={setForm} u={u} />;
      case 3: return <Section4 form={form} setForm={setForm} u={u} />;
      case 4: return <Section5 form={form} setForm={setForm} u={u} />;
      case 5: return <Section6 form={form} setForm={setForm} u={u} />;
      case 6: return <Section7 form={form} setForm={setForm} u={u} />;
      case 7: return <Section8 form={form} setForm={setForm} u={u} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="intake-form-page">
      {/* Progress Bar */}
      <div className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-lg font-bold text-[#2c3e50]">Unbox'D Autism — Client Intake Form</h1>
            <span className="text-sm text-gray-500">Step {step + 1} of {SECTIONS.length}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-[#C97B5A] h-2 rounded-full transition-all duration-300"
              style={{ width: `${((step + 1) / SECTIONS.length) * 100}%` }}
            />
          </div>
          {/* Section Tabs */}
          <div className="flex gap-1 mt-2 overflow-x-auto pb-1">
            {SECTIONS.map((s, i) => (
              <button
                key={s}
                onClick={() => setStep(i)}
                data-testid={`intake-tab-${i}`}
                className={`whitespace-nowrap px-3 py-1 text-xs rounded-full transition-colors ${i === step ? 'bg-[#C97B5A] text-white' : i < step ? 'bg-[#C97B5A]/20 text-[#C97B5A]' : 'bg-gray-100 text-gray-400'}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl text-[#2c3e50]">{SECTIONS[step]}</CardTitle>
          </CardHeader>
          <CardContent>{renderSection()}</CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6 pb-8">
          <Button
            variant="outline"
            onClick={() => setStep(s => s - 1)}
            disabled={step === 0}
            data-testid="intake-prev-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          {step < SECTIONS.length - 1 ? (
            <Button
              onClick={() => setStep(s => s + 1)}
              className="bg-[#C97B5A] hover:bg-[#b86a4a]"
              data-testid="intake-next-btn"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              className="bg-green-600 hover:bg-green-700"
              disabled={saving}
              data-testid="intake-submit-btn"
            >
              {saving ? 'Submitting...' : 'Submit Intake Form'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ====== SECTION 1: Family & Child Info ======
const Section1 = ({ form, setForm, u, user }) => (
  <div className="space-y-4">
    <Field label="Parent/Guardian Full Name" required>
      <Input data-testid="intake-parentName" value={form.parentName || user?.name || ''} onChange={u('parentName')} placeholder="Your full name" />
    </Field>
    <Field label="Relationship to Child">
      <Input data-testid="intake-relationship" value={form.relationship} onChange={u('relationship')} placeholder="e.g. Mother, Father, Grandparent" />
    </Field>
    <Field label="Phone Number">
      <Input data-testid="intake-phone" value={form.phone} onChange={u('phone')} placeholder="(555) 555-5555" />
    </Field>
    <Field label="City, State">
      <Input data-testid="intake-address" value={form.address} onChange={u('address')} placeholder="City, State" />
    </Field>
    <div className="border-t pt-4 mt-4">
      <p className="text-sm font-semibold text-[#2c3e50] mb-3">Child Information</p>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <Field label="Child's First Name" required>
        <Input data-testid="intake-childFirstName" value={form.childFirstName} onChange={u('childFirstName')} />
      </Field>
      <Field label="Child's Last Name">
        <Input data-testid="intake-childLastName" value={form.childLastName} onChange={u('childLastName')} />
      </Field>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <Field label="Date of Birth">
        <Input data-testid="intake-childDob" type="date" value={form.childDob} onChange={u('childDob')} />
      </Field>
      <Field label="Age">
        <Input data-testid="intake-childAge" value={form.childAge} onChange={u('childAge')} placeholder="e.g. 7" />
      </Field>
    </div>
    <Field label="Child's Pronouns">
      <div className="flex gap-2 flex-wrap">
        {['He/Him', 'She/Her', 'They/Them', 'Other'].map(p => (
          <label key={p} className={`px-4 py-2 rounded-full border cursor-pointer text-sm transition-colors ${form.childPronouns === p ? 'bg-[#C97B5A]/10 border-[#C97B5A] text-[#C97B5A]' : 'border-gray-200 hover:border-gray-300'}`}>
            <input type="radio" name="pronouns" value={p} checked={form.childPronouns === p} onChange={() => setForm({ ...form, childPronouns: p })} className="sr-only" />
            {p}
          </label>
        ))}
      </div>
    </Field>
    <Field label="Who Lives in the Home?">
      <Textarea data-testid="intake-householdMembers" value={form.householdMembers} onChange={u('householdMembers')} placeholder="List family members and their relationship to the child" rows={3} />
    </Field>
    <Field label="Siblings (names and ages)">
      <Textarea data-testid="intake-siblings" value={form.siblings} onChange={u('siblings')} placeholder="e.g. Emma (9), Jake (5)" rows={2} />
    </Field>
    <Field label="Additional Family Notes">
      <Textarea data-testid="intake-familyNotes" value={form.familyNotes} onChange={u('familyNotes')} placeholder="Anything else about your family structure that would be helpful to know" rows={3} />
    </Field>
  </div>
);

// ====== SECTION 2: Diagnoses & Development ======
const Section2 = ({ form, setForm, u }) => (
  <div className="space-y-5">
    <CheckboxGroup label="Current Diagnoses (select all that apply)" options={DIAGNOSES} selected={form.diagnoses} onChange={(v) => setForm({ ...form, diagnoses: v })} />
    {form.diagnoses.includes('Other') && (
      <Field label="Other Diagnosis">
        <Input value={form.diagnosisOther} onChange={u('diagnosisOther')} placeholder="Please specify" />
      </Field>
    )}
    <div className="grid grid-cols-2 gap-4">
      <Field label="Age at Diagnosis">
        <Input value={form.ageAtDiagnosis} onChange={u('ageAtDiagnosis')} placeholder="e.g. 3 years old" />
      </Field>
      <Field label="Diagnosing Provider">
        <Input value={form.diagnosingProvider} onChange={u('diagnosingProvider')} placeholder="Name or type of provider" />
      </Field>
    </div>
    <div className="border-t pt-4">
      <CheckboxGroup label="Previous Evaluations Completed (select all that apply, then summarize key results below)" options={EVALUATIONS} selected={form.evaluations} onChange={(v) => setForm({ ...form, evaluations: v })} />
    </div>
    <Field label="Evaluation Results / Key Findings">
      <Textarea value={form.evaluationResults} onChange={u('evaluationResults')} rows={4}
        placeholder="Please share what you remember:&#10;- Test scores or percentiles&#10;- Key findings or recommendations&#10;- Lab results or genetic findings&#10;- Areas of strength or concern identified" />
    </Field>
    <div className="border-t pt-4">
      <CheckboxGroup label="Previous/Current Therapies" options={THERAPIES} selected={form.therapies} onChange={(v) => setForm({ ...form, therapies: v })} />
    </div>
    {form.therapies.includes('Other') && (
      <Field label="Other Therapy">
        <Input value={form.therapyOther} onChange={u('therapyOther')} placeholder="Please specify" />
      </Field>
    )}
    <Field label="Therapy Details (duration, frequency, progress)">
      <Textarea value={form.therapyDetails} onChange={u('therapyDetails')} rows={3} placeholder="e.g. Speech therapy 2x/week for 2 years, seeing good progress with articulation" />
    </Field>
    <div className="border-t pt-4">
      <p className="text-sm font-semibold text-[#2c3e50] mb-3">Birth & Developmental History</p>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <Field label="Gestational Age at Birth">
        <Input value={form.gestationalAge} onChange={u('gestationalAge')} placeholder="e.g. 38 weeks" />
      </Field>
      <Field label="NICU Stay">
        <Input value={form.nicuStay} onChange={u('nicuStay')} placeholder="Yes/No — if yes, how long?" />
      </Field>
    </div>
    <Field label="Birth Complications">
      <Textarea value={form.birthComplications} onChange={u('birthComplications')} rows={2} placeholder="Any complications during pregnancy or delivery" />
    </Field>
    <div className="grid grid-cols-2 gap-4">
      <Field label="Age Walked">
        <Input value={form.ageWalked} onChange={u('ageWalked')} placeholder="e.g. 14 months" />
      </Field>
      <Field label="Age of First Words">
        <Input value={form.ageFirstWords} onChange={u('ageFirstWords')} placeholder="e.g. 18 months" />
      </Field>
    </div>
    <Field label="Any Regression?">
      <div className="flex gap-2 mb-2">
        {['No', 'Yes'].map(v => (
          <label key={v} className={`px-4 py-2 rounded-full border cursor-pointer text-sm ${form.regression === v ? 'bg-[#C97B5A]/10 border-[#C97B5A] text-[#C97B5A]' : 'border-gray-200'}`}>
            <input type="radio" name="regression" value={v} checked={form.regression === v} onChange={() => setForm({ ...form, regression: v })} className="sr-only" /> {v}
          </label>
        ))}
      </div>
      {form.regression === 'Yes' && (
        <Textarea value={form.regressionDetails} onChange={u('regressionDetails')} rows={2} placeholder="What skills were lost and at what age?" />
      )}
    </Field>
    <Field label="Family History of Neurodevelopmental Conditions">
      <Textarea value={form.familyHistory} onChange={u('familyHistory')} rows={2} placeholder="Any family members with autism, ADHD, learning disabilities, etc." />
    </Field>
  </div>
);

// ====== SECTION 3: Medical & Health ======
const Section3 = ({ form, setForm, u }) => (
  <div className="space-y-5">
    <Field label="Current Specialists (list provider name and type)">
      <Textarea value={form.currentSpecialists} onChange={u('currentSpecialists')} rows={3} placeholder="e.g. Dr. Smith — Pediatric Neurologist&#10;Dr. Jones — GI Specialist&#10;Dr. Lee — Pediatrician" />
    </Field>
    <CheckboxGroup label="GI Symptoms (select all that apply)" options={GI_SYMPTOMS} selected={form.giSymptoms} onChange={(v) => setForm({ ...form, giSymptoms: v })} />
    {form.giSymptoms.includes('Other') && (
      <Field label="Other GI Symptoms">
        <Input value={form.giOther} onChange={u('giOther')} placeholder="Please specify" />
      </Field>
    )}
    <div className="border-t pt-4">
      <p className="text-sm font-semibold text-[#2c3e50] mb-3">Sleep</p>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <Field label="Typical Bedtime">
        <Input value={form.sleepBedtime} onChange={u('sleepBedtime')} placeholder="e.g. 8:30 PM" />
      </Field>
      <Field label="Hours of Sleep per Night">
        <Input value={form.sleepHours} onChange={u('sleepHours')} placeholder="e.g. 9-10 hours" />
      </Field>
    </div>
    <Field label="Night Waking?">
      <Input value={form.nightWaking} onChange={u('nightWaking')} placeholder="How often? What helps them go back to sleep?" />
    </Field>
    <Field label="Sleep Aids Used">
      <Input value={form.sleepAids} onChange={u('sleepAids')} placeholder="e.g. Melatonin, weighted blanket, white noise" />
    </Field>
    <div className="border-t pt-4" />
    <Field label="Immune / Inflammatory History">
      <Textarea value={form.immuneHistory} onChange={u('immuneHistory')} rows={2} placeholder="Frequent ear infections, autoimmune conditions, chronic inflammation, etc." />
    </Field>
    <Field label="Known Food Allergies or Intolerances">
      <Textarea value={form.foodAllergies} onChange={u('foodAllergies')} rows={2} placeholder="List any known allergies or foods that cause reactions" />
    </Field>
    <Field label="Current Diet Description">
      <Textarea value={form.currentDiet} onChange={u('currentDiet')} rows={3} placeholder="Describe your child's typical daily diet — meals, snacks, drinks, any restrictions" />
    </Field>
    <Field label="Environmental Concerns or Exposures">
      <Textarea value={form.environmentalConcerns} onChange={u('environmentalConcerns')} rows={2} placeholder="Any concerns about mold, water quality, heavy metals, pesticides, etc." />
    </Field>
  </div>
);

// ====== SECTION 4: School & Learning ======
const Section4 = ({ form, setForm, u }) => (
  <div className="space-y-5">
    <div className="grid grid-cols-2 gap-4">
      <Field label="School Name">
        <Input value={form.schoolName} onChange={u('schoolName')} placeholder="Current school" />
      </Field>
      <Field label="Grade">
        <Input value={form.grade} onChange={u('grade')} placeholder="e.g. 3rd grade" />
      </Field>
    </div>
    <Field label="School Type">
      <div className="flex gap-2 flex-wrap">
        {['Public', 'Private', 'Charter', 'Homeschool', 'Other'].map(t => (
          <label key={t} className={`px-4 py-2 rounded-full border cursor-pointer text-sm ${form.schoolType === t ? 'bg-[#C97B5A]/10 border-[#C97B5A] text-[#C97B5A]' : 'border-gray-200'}`}>
            <input type="radio" name="schoolType" value={t} checked={form.schoolType === t} onChange={() => setForm({ ...form, schoolType: t })} className="sr-only" /> {t}
          </label>
        ))}
      </div>
    </Field>
    <Field label="IEP / 504 Status">
      <div className="flex gap-2 flex-wrap">
        {['IEP', '504 Plan', 'Both', 'Neither', 'In Process'].map(s => (
          <label key={s} className={`px-4 py-2 rounded-full border cursor-pointer text-sm ${form.iepStatus === s ? 'bg-[#C97B5A]/10 border-[#C97B5A] text-[#C97B5A]' : 'border-gray-200'}`}>
            <input type="radio" name="iepStatus" value={s} checked={form.iepStatus === s} onChange={() => setForm({ ...form, iepStatus: s })} className="sr-only" /> {s}
          </label>
        ))}
      </div>
    </Field>
    {(form.iepStatus === 'IEP' || form.iepStatus === 'Both') && (
      <Field label="Current IEP Goals Summary">
        <Textarea value={form.iepGoals} onChange={u('iepGoals')} rows={3} placeholder="Summarize your child's current IEP goals" />
      </Field>
    )}
    <CheckboxGroup label="Current Services at School" options={SCHOOL_SERVICES} selected={form.schoolServices} onChange={(v) => setForm({ ...form, schoolServices: v })} />
    {form.schoolServices.includes('Other') && (
      <Field label="Other School Services">
        <Input value={form.schoolServicesOther} onChange={u('schoolServicesOther')} placeholder="Please specify" />
      </Field>
    )}
    <CheckboxGroup label="Current Accommodations / Modifications" options={ACCOMMODATIONS} selected={form.accommodations} onChange={(v) => setForm({ ...form, accommodations: v })} />
    <Field label="Additional Accommodation Details">
      <Textarea value={form.accommodationDetails} onChange={u('accommodationDetails')} rows={3}
        placeholder="Share any details about how accommodations are being implemented:&#10;- Are accommodations being followed consistently?&#10;- Any that were requested but denied?&#10;- Anything not captured above?" />
    </Field>
    <Field label="Academic Strengths">
      <Textarea value={form.academicStrengths} onChange={u('academicStrengths')} rows={2} placeholder="What does your child excel at in school?" />
    </Field>
    <Field label="Academic Challenges">
      <Textarea value={form.academicChallenges} onChange={u('academicChallenges')} rows={2} placeholder="Where does your child struggle most?" />
    </Field>
  </div>
);

// ====== SECTION 5: Behavior & Regulation ======
const Section5 = ({ form, setForm, u }) => (
  <div className="space-y-5">
    <CheckboxGroup label="Primary Behavior Concerns (select all that apply)" options={BEHAVIOR_CONCERNS} selected={form.behaviorConcerns} onChange={(v) => setForm({ ...form, behaviorConcerns: v })} />
    {form.behaviorConcerns.includes('Other') && (
      <Field label="Other Behavior Concerns">
        <Input value={form.behaviorOther} onChange={u('behaviorOther')} placeholder="Please specify" />
      </Field>
    )}
    <Field label="Frequency of Most Challenging Behaviors">
      <Input value={form.behaviorFrequency} onChange={u('behaviorFrequency')} placeholder="e.g. Multiple times daily, weekly, situational" />
    </Field>
    <Field label="Known Triggers">
      <Textarea value={form.knownTriggers} onChange={u('knownTriggers')} rows={3} placeholder="What tends to set off challenging behaviors? (transitions, demands, sensory input, certain environments, etc.)" />
    </Field>
    <Field label="Strategies Already Tried">
      <Textarea value={form.strategiesTried} onChange={u('strategiesTried')} rows={3} placeholder="What have you tried so far? What worked, what didn't?" />
    </Field>
    <Field label="Sensory Profile">
      <div className="flex gap-2 flex-wrap mb-2">
        {['Sensory Seeking', 'Sensory Avoiding', 'Both/Mixed', 'Unsure'].map(s => (
          <label key={s} className={`px-4 py-2 rounded-full border cursor-pointer text-sm ${form.sensoryProfile === s ? 'bg-[#C97B5A]/10 border-[#C97B5A] text-[#C97B5A]' : 'border-gray-200'}`}>
            <input type="radio" name="sensory" value={s} checked={form.sensoryProfile === s} onChange={() => setForm({ ...form, sensoryProfile: s })} className="sr-only" /> {s}
          </label>
        ))}
      </div>
    </Field>
    <Field label="Sensory Details">
      <Textarea value={form.sensoryDetails} onChange={u('sensoryDetails')} rows={3} placeholder="Describe your child's sensory preferences and sensitivities (sounds, textures, movement, visual, taste, etc.)" />
    </Field>
    <Field label="Typical Meltdown Description">
      <Textarea value={form.meltdownDescription} onChange={u('meltdownDescription')} rows={3} placeholder="What does a typical meltdown look like? How long does it last?" />
    </Field>
    <Field label="What Helps During / After a Meltdown?">
      <Textarea value={form.meltdownHelps} onChange={u('meltdownHelps')} rows={2} placeholder="What strategies or supports help your child regulate?" />
    </Field>
    <Field label="Child's Strengths and Interests">
      <Textarea value={form.childStrengths} onChange={u('childStrengths')} rows={3} placeholder="What does your child love? What are they great at? What makes them light up?" />
    </Field>
  </div>
);

// ====== SECTION 6: Communication ======
const Section6 = ({ form, setForm, u }) => (
  <div className="space-y-5">
    <Field label="Current Verbal Level">
      <div className="flex gap-2 flex-wrap">
        {['Non-verbal', 'Limited words (1-20)', 'Phrases/short sentences', 'Full sentences', 'Conversational'].map(v => (
          <label key={v} className={`px-4 py-2 rounded-full border cursor-pointer text-sm ${form.verbalLevel === v ? 'bg-[#C97B5A]/10 border-[#C97B5A] text-[#C97B5A]' : 'border-gray-200'}`}>
            <input type="radio" name="verbal" value={v} checked={form.verbalLevel === v} onChange={() => setForm({ ...form, verbalLevel: v })} className="sr-only" /> {v}
          </label>
        ))}
      </div>
    </Field>
    <Field label="Receptive Language (Understanding)">
      <Textarea value={form.receptiveLanguage} onChange={u('receptiveLanguage')} rows={3} placeholder="How well does your child understand language?&#10;- Follows simple 1-step directions?&#10;- Understands complex multi-step directions?&#10;- Understands abstract concepts (time, emotions)?" />
    </Field>
    <Field label="Expressive Language Concerns">
      <Textarea value={form.expressiveConcerns} onChange={u('expressiveConcerns')} rows={3} placeholder="Any concerns about how your child expresses themselves? (word finding, grammar, pronunciation, echolalia, scripting, etc.)" />
    </Field>
    <Field label="AAC (Augmentative Communication) Use">
      <div className="flex gap-2 flex-wrap mb-2">
        {['No', 'Yes'].map(v => (
          <label key={v} className={`px-4 py-2 rounded-full border cursor-pointer text-sm ${form.aacUse === v ? 'bg-[#C97B5A]/10 border-[#C97B5A] text-[#C97B5A]' : 'border-gray-200'}`}>
            <input type="radio" name="aac" value={v} checked={form.aacUse === v} onChange={() => setForm({ ...form, aacUse: v })} className="sr-only" /> {v}
          </label>
        ))}
      </div>
      {form.aacUse === 'Yes' && (
        <Textarea value={form.aacDetails} onChange={u('aacDetails')} rows={2} placeholder="What device/app? How consistently is it used?" />
      )}
    </Field>
    <Field label="Social Communication Challenges">
      <Textarea value={form.socialCommunication} onChange={u('socialCommunication')} rows={3} placeholder="Eye contact, turn-taking, understanding social cues, making friends, etc." />
    </Field>
    <Field label="Speech-Language Services History">
      <Textarea value={form.slpHistory} onChange={u('slpHistory')} rows={2} placeholder="Current or past speech therapy? Duration, frequency, and progress?" />
    </Field>
  </div>
);

// ====== SECTION 7: Medications & Supplements ======
const Section7 = ({ form, setForm, u }) => (
  <div className="space-y-5">
    <Field label="Current Medications (name, dose, prescriber, reason)">
      <Textarea value={form.medications} onChange={u('medications')} rows={4}
        placeholder="List each medication:&#10;- Medication name — Dose — Prescribing doctor — Reason&#10;- e.g. Guanfacine — 1mg — Dr. Smith — Focus/attention&#10;&#10;Write 'None' if not applicable" />
    </Field>
    <CheckboxGroup label="Current Supplements (select all that apply)" options={SUPPLEMENTS} selected={form.supplements} onChange={(v) => setForm({ ...form, supplements: v })} />
    {form.supplements.includes('Other') && (
      <Field label="Other Supplements">
        <Input value={form.supplementOther} onChange={u('supplementOther')} placeholder="Please specify" />
      </Field>
    )}
    <Field label="Current Dietary Protocol">
      <div className="flex gap-2 flex-wrap">
        {['Standard Diet', 'Gluten-Free', 'Casein-Free', 'GFCF', 'SCD', 'Low Oxalate', 'Feingold', 'Other'].map(d => (
          <label key={d} className={`px-4 py-2 rounded-full border cursor-pointer text-sm ${form.dietaryProtocol === d ? 'bg-[#C97B5A]/10 border-[#C97B5A] text-[#C97B5A]' : 'border-gray-200'}`}>
            <input type="radio" name="diet" value={d} checked={form.dietaryProtocol === d} onChange={() => setForm({ ...form, dietaryProtocol: d })} className="sr-only" /> {d}
          </label>
        ))}
      </div>
    </Field>
  </div>
);

// ====== SECTION 8: Why Unbox'D ======
const Section8 = ({ form, setForm, u }) => (
  <div className="space-y-5">
    <Field label="How Did You Hear About Unbox'D?">
      <Input value={form.howHeard} onChange={u('howHeard')} placeholder="Social media, referral, Google, etc." />
    </Field>
    <Field label="What Brought You to Seek Coaching Now?">
      <Textarea value={form.whyNow} onChange={u('whyNow')} rows={3} placeholder="What's happening right now that made you reach out? What's the biggest challenge your family is facing?" />
    </Field>
    <Field label="Top 3 Goals for Your Child / Family" required>
      <Textarea value={form.topGoals} onChange={u('topGoals')} rows={4} placeholder="1.&#10;2.&#10;3." />
    </Field>
    <Field label="Timeline Expectations">
      <Input value={form.timeline} onChange={u('timeline')} placeholder="e.g. Looking for support over the next 6 months" />
    </Field>
    <Field label="Anything Else You'd Like to Share">
      <Textarea value={form.anythingElse} onChange={u('anythingElse')} rows={4} placeholder="This is your open space — share anything that doesn't fit neatly into the questions above. The more Robin knows, the better she can support your family." />
    </Field>
    <div className="border-t pt-4">
      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox
          checked={form.consent}
          onCheckedChange={(checked) => setForm({ ...form, consent: checked })}
          data-testid="intake-consent"
          className="mt-1"
        />
        <span className="text-sm text-gray-600">
          I understand that the information I provide will be used by Coach Robin to support my family through coaching and consulting services. I acknowledge that Unbox'D Autism does not provide therapy, medical, or legal services. All guidance is for informational and educational purposes only.
        </span>
      </label>
    </div>
  </div>
);

export default IntakeForm;
